import React, { useRef, useState } from "react";
import moment from "moment";
import {
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import LastPageIcon from "@material-ui/icons/LastPage";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import MoveDealMenus from "../layout/MoveDealMenus";
import calcIcon from "../../assets/images-v2/Icons/calc.svg";
import calcHoverIcon from "../../assets/images-v2/Icons/calc_hover.svg";
import duplicateIcon from "../../assets/images-v2/Icons/duplicate.svg";
import duplicateHoverIcon from "../../assets/images-v2/Icons/duplicate_hover.svg";
import trashIcon from "../../assets/images-v2/Icons/trash.svg";
import trashHoverIcon from "../../assets/images-v2/Icons/trash_hover.svg";
import manageDealIcon from "../../assets/images-v2/Icons/manage_deal.svg";
import searchIcon from "../../assets/images-v2/Icons/magnifying_glass.svg";
import taxableIcon from "../../assets/images-v2/Icons/red_alert.svg";
import taxExemptIcon from "../../assets/images-v2/Icons/checkbox.svg";
import * as customColors from "../../theme/customColors";

const useStyles = makeStyles(() => ({
  section: {
    boxSizing: "border-box",
    marginTop: "22px",
    minWidth: 0,
    width: "100%"
  },
  title: {
    color: "#1e3d66",
    fontWeight: 700,
    marginBottom: "12px",
    textAlign: "right"
  },
  tableToolbar: {
    alignItems: "flex-end",
    direction: "rtl",
    display: "flex",
    gap: "20px",
    justifyContent: "space-between",
    marginBottom: 0
  },
  tabs: {
    backgroundColor: "#e3e7eb",
    borderRadius: "18px 18px 0 0",
    direction: "rtl",
    display: "inline-flex",
    flexShrink: 0,
    overflow: "hidden",
    padding: "4px"
  },
  tab: {
    backgroundColor: "transparent",
    border: 0,
    borderRadius: "16px",
    color: "#1e3d66",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "17px",
    fontWeight: 700,
    minWidth: "170px",
    padding: "10px 22px",
    transition: "background-color 180ms ease, color 180ms ease"
  },
  activeTab: {
    background: "linear-gradient(180deg, #ffb109 0%, #ff9609 100%)",
    color: customColors.white
  },
  controls: {
    alignItems: "center",
    direction: "rtl",
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    justifyContent: "flex-start",
    marginBottom: "10px"
  },
  searchField: {
    width: "175px",
    "& .MuiOutlinedInput-root": {
      backgroundColor: customColors.white,
      borderRadius: "18px"
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 14px"
    }
  },
  searchButton: {
    alignItems: "center",
    background: "linear-gradient(180deg, #ffb109 0%, #ff9609 100%)",
    border: 0,
    borderRadius: "18px",
    color: customColors.white,
    cursor: "pointer",
    display: "flex",
    fontFamily: "inherit",
    fontSize: "17px",
    fontWeight: 700,
    gap: "7px",
    height: "36px",
    padding: "0 18px"
  },
  searchIcon: {
    height: "17px",
    width: "17px"
  },
  paper: {
    borderRadius: "10px",
    boxShadow: "0 2px 5px rgba(21, 44, 58, 0.25)",
    maxWidth: "100%",
    overflow: "auto",
    width: "100%"
  },
  table: {
    tableLayout: "fixed",
    width: "100%"
  },
  headerCell: {
    backgroundColor: "#1e3d66",
    borderBottom: 0,
    color: customColors.white,
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: 1.25,
    overflow: "hidden",
    padding: "10px 6px",
    whiteSpace: "normal"
  },
  dealNumberCell: {
    paddingInlineStart: "12px",
    width: "7%"
  },
  nameCell: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "12%"
  },
  lawyerCell: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "10%"
  },
  dateCell: {
    width: "9%"
  },
  bodyCell: {
    borderBottom: `1px solid ${customColors.lightGray}`,
    color: "#1e3d66",
    fontSize: "14px",
    overflow: "hidden",
    padding: "9px 6px",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  statusCell: {
    width: "11%"
  },
  deadlineCell: {
    width: "11%"
  },
  reportsCell: {
    width: "7%"
  },
  leftCompactCell: {
    width: "8%"
  },
  row: {
    cursor: "pointer",
    transition: "background-color 160ms ease",
    "&:hover": {
      backgroundColor: "#f4f8fb"
    }
  },
  selectedRow: {
    backgroundColor: "#fff1cf",
    boxShadow: `inset 0 0 0 2px ${customColors.orange}`,
    "&:hover": {
      backgroundColor: "#fff1cf"
    }
  },
  dealNumber: {
    fontWeight: 700
  },
  status: {
    alignItems: "center",
    border: "1px solid",
    borderRadius: "6px",
    display: "inline-flex",
    fontSize: "12px",
    fontWeight: 700,
    gap: "4px",
    maxWidth: "100%",
    overflow: "hidden",
    padding: "2px 6px"
  },
  taxableStatus: {
    backgroundColor: "#fbe8e8",
    borderColor: "#d86767",
    color: "#b74747"
  },
  taxExemptStatus: {
    backgroundColor: "#edf8e8",
    borderColor: "#76bd58",
    color: "#4f9e2f"
  },
  statusIcon: {
    flexShrink: 0,
    height: "12px",
    width: "12px"
  },
  actions: {
    alignItems: "center",
    direction: "ltr",
    display: "flex",
    gap: "2px",
    justifyContent: "center"
  },
  actionButton: {
    backgroundColor: "transparent",
    border: 0,
    cursor: "pointer",
    padding: "3px"
  },
  actionIcon: {
    display: "block",
    height: "18px",
    objectFit: "contain",
    width: "18px"
  },
  actionsCell: {
    width: "9%"
  },
  paginationCell: {
    borderBottom: 0,
    padding: 0
  },
  paginationActions: {
    display: "flex",
    marginLeft: "8px"
  },
  emptyCell: {
    color: customColors.darkGray,
    padding: "30px",
    textAlign: "center"
  },
  "@media only screen and (max-width: 1500px)": {
    headerCell: {
      fontSize: "13px",
      padding: "9px 5px"
    },
    bodyCell: {
      fontSize: "13px",
      padding: "8px 5px"
    },
    status: {
      fontSize: "11px",
      padding: "2px 4px"
    },
    actionIcon: {
      height: "16px",
      width: "16px"
    }
  },
  "@media only screen and (max-width: 1000px)": {
    headerCell: {
      fontSize: "11px",
      padding: "8px 4px"
    },
    bodyCell: {
      fontSize: "11px",
      padding: "7px 4px"
    },
    actions: {
      gap: 0
    },
    actionButton: {
      padding: "2px"
    },
    actionIcon: {
      height: "15px",
      width: "15px"
    },
    status: {
      fontSize: "10px",
      gap: "2px",
      padding: "1px 3px"
    },
    statusIcon: {
      height: "10px",
      width: "10px"
    }
  },
  "@media only screen and (max-width: 700px)": {
    tableToolbar: {
      alignItems: "stretch",
      flexDirection: "column"
    },
    controls: {
      marginBottom: 0
    },
    tab: {
      minWidth: "auto",
      padding: "8px 10px"
    },
    searchField: {
      width: "100%"
    },
    headerCell: {
      fontSize: "10px",
      padding: "6px 3px"
    },
    bodyCell: {
      fontSize: "10px",
      padding: "6px 3px"
    },
    actionIcon: {
      height: "14px",
      width: "14px"
    },
    status: {
      fontSize: "9px",
      padding: "1px 2px"
    },
    statusIcon: {
      height: "9px",
      width: "9px"
    }
  }
}));

const TableActionButton = (props) => {
  const classes = useStyles();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Tooltip title={props.label}>
      <button
        aria-label={props.label}
        className={classes.actionButton}
        onBlur={() => setIsHovered(false)}
        onClick={props.onClick}
        onFocus={() => setIsHovered(true)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        type="button"
      >
        <img alt="" className={classes.actionIcon} src={isHovered && props.hoverIcon ? props.hoverIcon : props.icon} />
      </button>
    </Tooltip>
  );
};

const PaginationActions = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const isFirstPage = props.page === 0;
  const isLastPage = props.page >= Math.ceil(props.count / props.rowsPerPage) - 1;

  return (
    <div className={classes.paginationActions}>
      <IconButton aria-label="עמוד ראשון" disabled={isFirstPage} onClick={(event) => props.onChangePage(event, 0)}>
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton aria-label="עמוד קודם" disabled={isFirstPage} onClick={(event) => props.onChangePage(event, props.page - 1)}>
        {theme.direction === "rtl" ? <KeyboardArrowRightIcon /> : <KeyboardArrowLeftIcon />}
      </IconButton>
      <IconButton aria-label="עמוד הבא" disabled={isLastPage} onClick={(event) => props.onChangePage(event, props.page + 1)}>
        {theme.direction === "rtl" ? <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon />}
      </IconButton>
      <IconButton
        aria-label="עמוד אחרון"
        disabled={isLastPage}
        onClick={(event) => props.onChangePage(event, Math.max(0, Math.ceil(props.count / props.rowsPerPage) - 1))}
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
};

const getBtaxCells = (deal, classes) => {
  const manage = deal.manage || {};
  const isTaxable = Boolean(deal.hasTaxToPay);

  return [
    <span className={classes.dealNumber}>{deal.prettyId || ""}</span>,
    manage.dealDescription || "",
    deal.fullName || "",
    moment(deal.dateCreated).format("DD/MM/YYYY"),
    <span className={`${classes.status} ${isTaxable ? classes.taxableStatus : classes.taxExemptStatus}`}>
      <img alt="" className={classes.statusIcon} src={isTaxable ? taxableIcon : taxExemptIcon} />
      {isTaxable ? "חייב במס" : "לא חייב במס"}
    </span>,
    deal.lastReportDate ? moment(deal.lastReportDate).format("DD/MM/YYYY") : "",
    manage.hasMasShevachReportsSubmitted ? "כן" : "לא",
    manage.hasEaaratAzhara ? "כן" : "לא",
    manage.haitelHashbacha && manage.masRechisha && manage.masShavach ? "כן" : "לא"
  ];
};

const getBasicCells = (deal, classes) => {
  return [
    <span className={classes.dealNumber}>{deal.prettyId || ""}</span>,
    deal.manage?.dealDescription || "",
    deal.fullName || "",
    moment(deal.dateCreated).format("DD/MM/YYYY")
  ];
};

const MyDealsTableSection = (props) => {
  const classes = useStyles();
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [dealSharingOptionsEvent, setDealSharingOptionsEvent] = useState(null);
  const dealIdBeingSharedOrTransferred = useRef(null);
  const activeTab = props.tabs.find((tab) => tab.value === props.activeDealType);
  const isBtax = props.activeDealType === "btax";
  const headers = isBtax
    ? ["מס׳ עסקה", "שם עסקה", "עורך דין", "תאריך יצירה", "סטטוס מס", "מועד אחרון לדיווח", "דיווחים", "הערות אזהרה", "אישורי מיסים"]
    : ["מס׳ עסקה", "שם עסקה", "עורך דין", "תאריך יצירה"];
  const columnCount = headers.length + 1;

  const getDataColumnClass = (index, isHeader) => {
    const cellClasses = [isHeader ? classes.headerCell : classes.bodyCell];
    if (index === 0) {
      cellClasses.push(classes.dealNumberCell);
    }
    if (index === 1) {
      cellClasses.push(classes.nameCell);
    }
    if (index === 2) {
      cellClasses.push(classes.lawyerCell);
    }
    if (index === 3) {
      cellClasses.push(classes.dateCell);
    }
    if (isBtax && index === 4) {
      cellClasses.push(classes.statusCell);
    }
    if (isBtax && index === 5) {
      cellClasses.push(classes.deadlineCell);
    }
    if (isBtax && index === 6) {
      cellClasses.push(classes.reportsCell);
    }
    if (isBtax && index > 6) {
      cellClasses.push(classes.leftCompactCell);
    }
    return cellClasses.join(" ");
  };

  const handleOwnershipMenuOpen = (event, deal) => {
    dealIdBeingSharedOrTransferred.current = {
      dealId: deal._id,
      belongToEmail: deal.belongToEmail
    };
    setDealSharingOptionsEvent(event.currentTarget);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") props.onSearch();
  };

  return (
    <section className={classes.section}>
      <Typography className={classes.title} variant="h4">
        העסקאות שלי - {activeTab?.title || ""}
      </Typography>
      <div className={classes.tableToolbar}>
        <div className={classes.tabs} role="tablist" aria-label="סוג עסקה">
          {props.tabs.map((tab) => (
            <button
              aria-selected={props.activeDealType === tab.value}
              className={`${classes.tab} ${props.activeDealType === tab.value ? classes.activeTab : ""}`}
              key={tab.value}
              onClick={() => props.onTabChange(tab.value)}
              role="tab"
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className={classes.controls}>
          <TextField
            className={classes.searchField}
            inputProps={{ inputMode: "numeric" }}
            onChange={(event) => props.onSearchFilterChange("dealPrettyId", event.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="אתר מספר עסקה"
            size="small"
            value={props.searchFilters.dealPrettyId}
            variant="outlined"
          />
          <TextField
            className={classes.searchField}
            onChange={(event) => props.onSearchFilterChange("dealName", event.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="אתר שם עסקה"
            size="small"
            value={props.searchFilters.dealName}
            variant="outlined"
          />
          <button className={classes.searchButton} onClick={props.onSearch} type="button">
            <img alt="" className={classes.searchIcon} src={searchIcon} />
            חפש/י
          </button>
        </div>
      </div>
      <Paper className={classes.paper}>
        <TableContainer>
          <Table className={classes.table} aria-label={`העסקאות שלי - ${activeTab?.title || ""}`}>
            <TableHead>
              <TableRow>
                {headers.map((header, index) => (
                  <TableCell
                    align="right"
                    className={getDataColumnClass(index, true)}
                    key={header}
                  >
                    {header}
                  </TableCell>
                ))}
                <TableCell align="center" className={`${classes.headerCell} ${classes.actionsCell}`}>
                  פעולות
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props.isLoading ? (
                <TableRow>
                  <TableCell className={classes.emptyCell} colSpan={columnCount}>
                    <CircularProgress color="secondary" size={30} />
                  </TableCell>
                </TableRow>
              ) : null}
              {!props.isLoading && props.deals.length === 0 ? (
                <TableRow>
                  <TableCell className={classes.emptyCell} colSpan={columnCount}>
                    לא נמצאו עסקאות
                  </TableCell>
                </TableRow>
              ) : null}
              {!props.isLoading
                ? props.deals.map((deal) => {
                    const cells = isBtax ? getBtaxCells(deal, classes) : getBasicCells(deal, classes);
                    return (
                      <TableRow
                        aria-selected={selectedDealId === deal._id}
                        className={`${classes.row} ${selectedDealId === deal._id ? classes.selectedRow : ""}`}
                        key={deal._id}
                        onClick={() => setSelectedDealId(deal._id)}
                        onDoubleClick={() => props.onOpenDeal(deal)}
                      >
                        {cells.map((cell, index) => (
                          <TableCell
                            align="right"
                            className={getDataColumnClass(index, false)}
                            key={`${deal._id}-${headers[index]}`}
                          >
                            {cell}
                          </TableCell>
                        ))}
                        <TableCell align="center" className={`${classes.bodyCell} ${classes.actionsCell}`}>
                          <div className={classes.actions}>
                            <TableActionButton
                              hoverIcon={trashHoverIcon}
                              icon={trashIcon}
                              label="מחק"
                              onClick={() => props.onDeleteDeal(deal)}
                            />
                            <TableActionButton
                              hoverIcon={duplicateHoverIcon}
                              icon={duplicateIcon}
                              label="שכפל"
                              onClick={() => props.onCopyDeal(deal)}
                            />
                            <TableActionButton
                              hoverIcon={calcHoverIcon}
                              icon={calcIcon}
                              label="חשב"
                              onClick={() => props.onOpenDeal(deal)}
                            />
                            {isBtax && props.officeLawyers.length ? (
                              <TableActionButton
                                icon={manageDealIcon}
                                label="ניהול עסקה"
                                onClick={(event) => handleOwnershipMenuOpen(event, deal)}
                              />
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                : null}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  ActionsComponent={PaginationActions}
                  className={classes.paginationCell}
                  colSpan={columnCount}
                  count={props.dealsAllCount}
                  onChangePage={(event, page) => props.onPageChange(page)}
                  page={props.page}
                  rowsPerPage={10}
                  rowsPerPageOptions={[10]}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
      {isBtax && props.officeLawyers.length ? (
        <MoveDealMenus
          cancelShareDeal={props.cancelShareDeal}
          currentLawyerEmail={props.currentLawyerEmail}
          dealIdBeingSharedOrTransferred={dealIdBeingSharedOrTransferred.current}
          dealSharingOptionsEvent={dealSharingOptionsEvent}
          dealsPrettyIdAndDesc={props.dealsPrettyIdAndDesc}
          officeLawyers={props.officeLawyers}
          setDealSharingOptionsEvent={setDealSharingOptionsEvent}
          shareDeal={props.shareDeal}
          transferDeal={props.transferDeal}
        />
      ) : null}
    </section>
  );
};

export default MyDealsTableSection;
